import { z } from 'zod';

/**
 * User-level personalization settings
 * Stored in Zustand store + optionally in WorkOS user metadata
 */
export const UserPersonalizationSettingsSchema = z.object({
  displayName: z.string().optional(),
  jobTitle: z.string().optional(),
  specialtyOverride: z.string().optional(), // Combines with org specialty (shows as "User Spec (Department: Org Spec)")
  additionalCptCodes: z.array(z.string()).optional(), // Added to org codes (combined list)
  additionalTaxonomies: z.array(z.string()).optional(), // Added to org taxonomies (combined list)
  customInstructions: z.string().optional(), // Custom AI instructions/preferences
  otherDetails: z.string().optional(), // Unstructured additional info about the user
});

export type UserPersonalizationSettings = z.infer<typeof UserPersonalizationSettingsSchema>;

/**
 * Organization-level personalization settings
 * Stored in WorkOS organization metadata (admin-only)
 */
export const OrgPersonalizationSettingsSchema = z.object({
  organizationDisplayName: z.string(),
  primarySpecialty: z.string(),
  commonCptCodes: z.array(z.string()),
  providerTaxonomies: z.array(z.string()),
  otherDetails: z.string().optional(), // Unstructured additional info about the organization
  personalizedAt: z.string().optional(), // ISO timestamp when first configured
});

export type OrgPersonalizationSettings = z.infer<typeof OrgPersonalizationSettingsSchema>;

/**
 * Personalization mode: which preset is active
 */
export const PersonalizationModeSchema = z.enum(['org', 'custom']);

export type PersonalizationMode = z.infer<typeof PersonalizationModeSchema>;

/**
 * Complete personalization state for the store
 */
export const PersonalizationStateSchema = z.object({
  activeMode: PersonalizationModeSchema,
  userSettings: UserPersonalizationSettingsSchema,
  orgSettings: OrgPersonalizationSettingsSchema.nullable(),
});

export type PersonalizationState = z.infer<typeof PersonalizationStateSchema>;

/**
 * Effective settings computed based on active mode
 * This is what gets injected into AI prompts
 */
export const EffectivePersonalizationSchema = z.object({
  name: z.string(),
  jobTitle: z.string().optional(),
  organizationName: z.string(),
  specialty: z.string(),
  cptCodes: z.array(z.string()),
  taxonomies: z.array(z.string()),
  customInstructions: z.string().optional(),
  otherDetails: z.string().optional(), // Combined user + org other details
});

export type EffectivePersonalization = z.infer<typeof EffectivePersonalizationSchema>;

/**
 * API Input/Output schemas
 */

// GET /personalization/user response
export const GetUserPersonalizationOutputSchema = z.object({
  settings: UserPersonalizationSettingsSchema,
  activeMode: PersonalizationModeSchema,
});

export type GetUserPersonalizationOutput = z.infer<typeof GetUserPersonalizationOutputSchema>;

// POST /personalization/user request
export const UpdateUserPersonalizationInputSchema = z.object({
  settings: UserPersonalizationSettingsSchema,
  activeMode: PersonalizationModeSchema.optional(),
});

export type UpdateUserPersonalizationInput = z.infer<typeof UpdateUserPersonalizationInputSchema>;

// POST /personalization/user response
export const UpdateUserPersonalizationOutputSchema = z.object({
  success: z.boolean(),
  settings: UserPersonalizationSettingsSchema,
});

export type UpdateUserPersonalizationOutput = z.infer<typeof UpdateUserPersonalizationOutputSchema>;

// GET /personalization/org response (admin-only)
export const GetOrgPersonalizationOutputSchema = z.object({
  settings: OrgPersonalizationSettingsSchema.nullable(),
  hasSettings: z.boolean(),
});

export type GetOrgPersonalizationOutput = z.infer<typeof GetOrgPersonalizationOutputSchema>;

// POST /personalization/org request (admin-only)
export const UpdateOrgPersonalizationInputSchema = z.object({
  settings: OrgPersonalizationSettingsSchema,
});

export type UpdateOrgPersonalizationInput = z.infer<typeof UpdateOrgPersonalizationInputSchema>;

// POST /personalization/org response (admin-only)
export const UpdateOrgPersonalizationOutputSchema = z.object({
  success: z.boolean(),
  settings: OrgPersonalizationSettingsSchema,
});

export type UpdateOrgPersonalizationOutput = z.infer<typeof UpdateOrgPersonalizationOutputSchema>;

/**
 * Onboarding flow types
 */
export const OnboardingStepSchema = z.enum([
  'welcome',
  'user-info',
  'org-setup',
  'confirmation',
  'complete',
]);

export type OnboardingStep = z.infer<typeof OnboardingStepSchema>;

export const OnboardingStatusSchema = z.object({
  completed: z.boolean(),
  completedAt: z.string().optional(), // ISO timestamp
  skipped: z.boolean(),
  skippedAt: z.string().optional(), // ISO timestamp
  currentStep: OnboardingStepSchema.optional(),
});

export type OnboardingStatus = z.infer<typeof OnboardingStatusSchema>;

export const OnboardingCollectedDataSchema = z.object({
  // User info
  displayName: z.string().optional(),
  jobTitle: z.string().optional(),
  customInstructions: z.string().optional(),
  userOtherDetails: z.string().optional(),

  // Org info (for admins)
  organizationDisplayName: z.string().optional(),
  primarySpecialty: z.string().optional(),
  commonCptCodes: z.array(z.string()).optional(),
  providerTaxonomies: z.array(z.string()).optional(),
  orgOtherDetails: z.string().optional(),
});

export type OnboardingCollectedData = z.infer<typeof OnboardingCollectedDataSchema>;
